// ChapterUploadModal.tsx
// NOTE: Update frontend/src/__tests__/ChapterUploadModal.test.tsx and run
// `../scripts/test-frontend.sh ChapterUploadModal` when editing this modal.
import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { Modal, Button, Alert, Table, Select, message, Space, Typography, Divider, Tooltip, theme } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TourProps } from 'antd';
import { UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { User } from 'firebase/auth';
import type { Chapter } from './types';
import { getAuthHeaders } from './utils/authHeaders';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from './tours/AppTour';
import { useTutorialSandbox } from './tutorials/TutorialSandboxProvider';
import { tutorialVideoLinks } from './tutorialVideoLinks';

const { Text, Paragraph } = Typography;

const API_BASE = resolveFrontendApiBase();

type PreviewRow = {
  temp_id: string;
  title: string;
  word_count: number;
  snippet: string;
  chapter_number: number;
};

type ReplacementSummary = {
  tempId: string;
  targetGuid: string;
  targetLabel: string;
  uploadTitle: string;
};

type ChapterSelection = {
  tempId: string;
  targetChapterGuid: string | null;
  expectedVersion?: string;
};

interface ChapterUploadModalProps {
  visible: boolean;
  onClose: () => void;
  bookId: string;
  userId?: string;
  authUser?: User | null;
  existingChapters: Chapter[];
  onSuccess: () => void;
  isEpubUpload?: boolean;
  onUploadProcessingChange?: (inProgress: boolean) => void;
  hasSeenUploadTour?: boolean;
  onMarkUploadTourSeen?: () => void;
  tourFlagsInitialized?: boolean;
}

const ChapterUploadModal: React.FC<ChapterUploadModalProps> = ({
  visible,
  onClose,
  bookId,
  userId,
  authUser,
  existingChapters,
  onSuccess,
  isEpubUpload = false,
  onUploadProcessingChange,
  hasSeenUploadTour = false,
  onMarkUploadTourSeen,
  tourFlagsInitialized = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [skippedChapters, setSkippedChapters] = useState<number[]>([]);
  const [chapterVersions, setChapterVersions] = useState<Record<string, string>>({});
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isReplaceConfirmVisible, setIsReplaceConfirmVisible] = useState(false);
  const [replacementSummaries, setReplacementSummaries] = useState<ReplacementSummary[]>([]);
  const [pendingSelections, setPendingSelections] = useState<ChapterSelection[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadTourOpen, setUploadTourOpen] = useState(false);
  const [uploadTourStep, setUploadTourStep] = useState(0);
  const [uploadTourAcknowledged, setUploadTourAcknowledged] = useState<boolean>(!!hasSeenUploadTour);
  const autoUploadTourTriggeredRef = useRef(false);
	  const modalContentRef = useRef<HTMLDivElement | null>(null);
	  const tourFallbackRef = useRef<HTMLElement | null>(null);
	  const { token } = theme.useToken();
	  const allowAppend = !isEpubUpload;
	  const requiredFileCopy = isEpubUpload ? 'Select an EPUB file first.' : 'Select a DOCX or EPUB file first.';
	  const tutorialSandbox = useTutorialSandbox();
	  const tutorialFixturePayload = tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture'
	    ? tutorialSandbox.fixturePayload
	    : null;

  useEffect(() => {
    if (!visible) {
      setFile(null);
      setPreviewRows([]);
      setMapping({});
      setSkippedChapters([]);
      setChapterVersions({});
      setPreviewError(null);
      setPreviewToken(null);
      setFileKey(prev => prev + 1);
      setIsDragOver(false);
      setIsReplaceConfirmVisible(false);
      setReplacementSummaries([]);
      setPendingSelections(null);
      setUploadTourOpen(false);
      setUploadTourStep(0);
      autoUploadTourTriggeredRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    if (allowAppend) {
      return;
    }
    setMapping(prev => {
      let changed = false;
      const updated: Record<string, string> = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (value === 'NEW') {
          updated[key] = 'SKIP';
          changed = true;
        } else {
          updated[key] = value;
        }
      });
      return changed ? updated : prev;
    });
  }, [allowAppend]);

  useEffect(() => {
    onUploadProcessingChange?.(isSubmitting);
  }, [isSubmitting, onUploadProcessingChange]);

  useEffect(() => {
    return () => {
      onUploadProcessingChange?.(false);
    };
  }, [onUploadProcessingChange]);

  useEffect(() => {
    if (hasSeenUploadTour) {
      setUploadTourAcknowledged(true);
    }
  }, [hasSeenUploadTour]);

  const markUploadTourSeenLocal = useCallback(() => {
    if (uploadTourAcknowledged) {
      return;
    }
    setUploadTourAcknowledged(true);
    onMarkUploadTourSeen?.();
  }, [uploadTourAcknowledged, onMarkUploadTourSeen]);

  const handleUploadTourDismiss = useCallback(() => {
    setUploadTourOpen(false);
    setUploadTourStep(0);
    markUploadTourSeenLocal();
  }, [markUploadTourSeenLocal]);

  const handleOpenUploadTour = useCallback(() => {
    autoUploadTourTriggeredRef.current = true;
    setUploadTourStep(0);
    setUploadTourOpen(true);
  }, []);

  useEffect(() => {
    if (!visible) return;
    if (!tourFlagsInitialized) return;
    if (uploadTourAcknowledged) return;
    if (autoUploadTourTriggeredRef.current) return;
    autoUploadTourTriggeredRef.current = true;
    const timer = window.setTimeout(() => {
      setUploadTourStep(0);
      setUploadTourOpen(true);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [visible, tourFlagsInitialized, uploadTourAcknowledged]);

  useEffect(() => {
    if (!visible && uploadTourOpen) {
      handleUploadTourDismiss();
    }
  }, [visible, uploadTourOpen, handleUploadTourDismiss]);

  const getFallbackTarget = useCallback((): HTMLElement => {
    if (modalContentRef.current) {
      return modalContentRef.current;
    }
    if (typeof document !== 'undefined') {
      const modalEl = document.querySelector('.chapter-upload-modal .ant-modal-content');
      if (modalEl instanceof HTMLElement) {
        return modalEl;
      }
      const bodyEl = document.body;
      if (bodyEl) {
        return bodyEl;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current = document.createElement('div');
        tourFallbackRef.current.style.position = 'fixed';
        tourFallbackRef.current.style.top = '0';
        tourFallbackRef.current.style.left = '0';
        if (document.body) {
          document.body.appendChild(tourFallbackRef.current);
        }
      }
      return tourFallbackRef.current;
    }
    if (!tourFallbackRef.current) {
      tourFallbackRef.current = {} as HTMLElement;
    }
    return tourFallbackRef.current;
  }, []);

  const findTourElement = useCallback((ids: string[]): HTMLElement | null => {
    if (typeof document === 'undefined') {
      return null;
    }
    for (const id of ids) {
      const el = document.querySelector(`[data-tour-id="${id}"]`);
      if (el instanceof HTMLElement) {
        return el;
      }
    }
    return null;
  }, []);

  const resolveTourTarget = useCallback(
    (primaryIds: string[], fallbackIds?: string[]) => {
      return (
        findTourElement(primaryIds) ??
        (fallbackIds ? findTourElement(fallbackIds) : null) ??
        getFallbackTarget()
      );
    },
    [findTourElement, getFallbackTarget]
  );

  const renderTourPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const uploadTourSteps = useMemo<TourProps['steps']>(() => {
    const formatCopy = allowAppend
      ? 'Word uploads can append brand-new chapters or overwrite existing ones. EPUB files can still replace chapters if you prefer.'
      : 'Because this project originated from an EPUB, replacements must stay one-to-one. Upload another EPUB to overwrite existing chapters while keeping the structure intact.';
    const mappingCopy = allowAppend
      ? 'Use “Append as new chapter” to drop Word content at the end, or select an existing chapter to overwrite it (translations and proofreads reset when you replace).'
      : 'Append is disabled for EPUB projects, so each uploaded chapter needs a destination to replace. We’ll warn you before anything gets overwritten.';
    const footerCopy = allowAppend
      ? 'Only the chapters you mapped will replace existing ones; appended chapters land at the end.'
      : 'Every saved replacement overwrites the paired chapter along with its translated and proofread versions.';

    return [
      {
        title: 'Drop in your manuscript',
        description: renderTourPanel(
          <>
            Drag & drop your updated file here or click Browse. {formatCopy}
            <AppTourVideoLink href={tutorialVideoLinks.chapterEditing}>
              Watch the chapter editing tutorial
            </AppTourVideoLink>
          </>
        ),
        target: () => resolveTourTarget(['chapter-upload-dropzone']),
        placement: 'bottom',
      },
      {
        title: 'Preview before saving',
        description: renderTourPanel(
          <>
            Generate a preview first to confirm chapters and word counts. This keeps accidental overwrites from slipping through.
          </>
        ),
        target: () => resolveTourTarget(['chapter-upload-preview-action'], ['chapter-upload-footer']),
        placement: 'bottom',
      },
      previewRows.length > 0
        ? {
            title: 'Review the summary',
            description: renderTourPanel(
              <>
                We list chapter snippets, word counts, and skipped sections so you can double-check what we found before mapping anything.
              </>
            ),
            target: () => resolveTourTarget(['chapter-upload-summary']),
            placement: 'bottom',
          }
        : null,
      previewRows.length > 0
        ? {
            title: 'Map every chapter',
            description: renderTourPanel(
              <>
                Decide whether each upload should replace an existing chapter or be skipped. {mappingCopy}
              </>
            ),
            target: () => resolveTourTarget(['chapter-upload-mapping'], ['chapter-upload-dropzone']),
            placement: 'left',
          }
        : null,
      previewRows.length > 0 && !allowAppend
        ? {
            title: 'EPUB replacements only',
            description: renderTourPanel(
              <>
                EPUB projects must replace existing chapters one-to-one. Appending is disabled to protect the original structure.
              </>
            ),
            target: () => resolveTourTarget(['chapter-upload-mapping'], ['chapter-upload-dropzone']),
            placement: 'left',
          }
        : null,
      previewRows.length > 0
        ? {
            title: 'Replacing resets work',
            description: renderTourPanel(
              <>
                Replaced chapters reset their translated and proofread versions. Keep this in mind before saving.
              </>
            ),
            target: () => resolveTourTarget(['chapter-upload-mapping'], ['chapter-upload-dropzone']),
            placement: 'left',
          }
        : null,
      previewRows.length > 0
        ? {
            title: 'Save when you’re ready',
            description: renderTourPanel(
              <>
                Generate a preview after every new file, then click <strong>Save Changes</strong> to commit. {footerCopy}
              </>
            ),
            target: () => resolveTourTarget(['chapter-upload-save-action'], ['chapter-upload-footer']),
            placement: 'left',
          }
        : null,
    ].filter(Boolean) as any[];
  }, [allowAppend, previewRows.length, resolveTourTarget, renderTourPanel]);

  const existingChapterOptions = useMemo(() => {
    const sorted = [...(existingChapters || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    return sorted.map(chapter => ({
      value: chapter.chapter_guid,
      label: `${chapter.order || 0}. ${chapter.chapter_name}`,
    }));
  }, [existingChapters]);

  const existingChapterMap = useMemo(() => {
    const map: Record<string, { label: string; chapter_name: string }> = {};
    (existingChapters || []).forEach(chapter => {
      const order = chapter.order || 0;
      map[chapter.chapter_guid] = {
        label: `${order || 0}. ${chapter.chapter_name}`,
        chapter_name: chapter.chapter_name,
      };
    });
    return map;
  }, [existingChapters]);

  const selectedTargets = useMemo(() => {
    const taken = new Set<string>();
    Object.values(mapping).forEach(value => {
      if (value && value !== 'NEW' && value !== 'SKIP') {
        taken.add(value);
      }
    });
    return taken;
  }, [mapping]);

  const previewRowMap = useMemo(() => {
    const map: Record<string, PreviewRow> = {};
    previewRows.forEach(row => {
      map[row.temp_id] = row;
    });
    return map;
  }, [previewRows]);

  const handleNewFile = (nextFile: File | null) => {
    setPreviewRows([]);
    setMapping({});
    setSkippedChapters([]);
    setChapterVersions({});
    setPreviewError(null);
    setPreviewToken(null);
    setIsDragOver(false);
    setFile(nextFile);
  };

  const isFileTypeAllowed = (nextFile: File) => {
    const filename = (nextFile?.name || '').toLowerCase();
    if (!filename) {
      return false;
    }
    if (isEpubUpload) {
      return filename.endsWith('.epub');
    }
    return filename.endsWith('.docx') || filename.endsWith('.epub');
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] || null;
    if (nextFile && !isFileTypeAllowed(nextFile)) {
      message.warning(isEpubUpload ? 'Please select an EPUB file.' : 'Please select a DOCX or EPUB file.');
      event.target.value = '';
      return;
    }
    handleNewFile(nextFile);
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const nextFile = event.dataTransfer?.files?.[0];
    if (!nextFile) {
      return;
    }
    if (!isFileTypeAllowed(nextFile)) {
      message.warning(isEpubUpload ? 'Please drop an EPUB file.' : 'Please drop a DOCX or EPUB file.');
      return;
    }
    handleNewFile(nextFile);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setIsDragOver(false);
    }
  };

	  const handleGeneratePreview = async () => {
	    if (!userId) {
	      message.error('Please sign in before uploading new chapters.');
	      return;
	    }
    if (!file) {
	      message.error(requiredFileCopy);
	      return;
	    }
	    const chapterUploadFixturePayload = tutorialFixturePayload;
	    if (
	      bookId === 'tutorial-book'
	      && chapterUploadFixturePayload
	      && Array.isArray(chapterUploadFixturePayload.chapterUploadPreviewRows)
	    ) {
	      const rows = chapterUploadFixturePayload.chapterUploadPreviewRows as PreviewRow[];
	      setPreviewRows(rows);
	      setSkippedChapters(Array.isArray(chapterUploadFixturePayload.chapterUploadSkippedChapters)
	        ? chapterUploadFixturePayload.chapterUploadSkippedChapters as number[]
	        : []);
	      setChapterVersions(
	        chapterUploadFixturePayload.chapterUploadChapterVersions
	        && typeof chapterUploadFixturePayload.chapterUploadChapterVersions === 'object'
	          ? chapterUploadFixturePayload.chapterUploadChapterVersions as Record<string, string>
	          : {}
	      );
	      setPreviewToken(String(chapterUploadFixturePayload.chapterUploadPreviewToken || 'tutorial-preview-token'));
	      const initialMapping: Record<string, string> = {};
	      rows.forEach((row, index) => {
	        if (index === 0 && existingChapters[0]?.chapter_guid) {
	          initialMapping[row.temp_id] = existingChapters[0].chapter_guid;
	        } else if (index === 1 && allowAppend) {
	          initialMapping[row.temp_id] = 'NEW';
	        } else {
	          initialMapping[row.temp_id] = 'SKIP';
	        }
	      });
	      setMapping(initialMapping);
	      setPreviewError(null);
	      return;
	    }
	    setIsPreviewLoading(true);
    setPreviewError(null);
    setPreviewToken(null);
    try {
      const authHeaders = await getAuthHeaders(authUser);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'replace');
      formData.append('user_id', userId);

      const response = await axios.post(`${API_BASE}/uploadedbooks/${bookId}/chapter-upload/preview`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 5,
      });

      const rows: PreviewRow[] = response.data?.chapters || [];
      setPreviewRows(rows);
      setSkippedChapters(response.data?.skipped_chapters || []);
      setChapterVersions(response.data?.chapter_versions || {});
      setPreviewToken(response.data?.preview_token || null);
      const initialMapping: Record<string, string> = {};
      rows.forEach(row => {
        initialMapping[row.temp_id] = 'SKIP';
      });
      setMapping(initialMapping);
      if (!rows.length) {
        setPreviewError('No chapters were detected in this upload.');
      }
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error.message || 'Unable to parse the upload.';
      setPreviewError(detail);
      setPreviewToken(null);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const performCommit = async (selectionsPayload: ChapterSelection[]) => {
    if (!userId) {
      message.error('Please sign in before uploading new chapters.');
      return;
    }
    if (!file) {
      message.error(requiredFileCopy);
      return;
    }
    setIsSubmitting(true);
    try {
      const authHeaders = await getAuthHeaders(authUser);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_id', userId);
    const payload: Record<string, any> = {
      mode: 'replace',
      selections: selectionsPayload,
    };
      if (previewToken) {
        payload.previewToken = previewToken;
      }
      formData.append('payload', JSON.stringify(payload));

      await axios.post(`${API_BASE}/uploadedbooks/${bookId}/chapter-upload/commit`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
        timeout: 1000 * 60 * 10,
      });

      message.success('Chapters updated successfully.');
      onSuccess();
      setPreviewToken(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message || error.message || 'Failed to save chapters.';
      message.error(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasReplacementSelections = useMemo(() => (
    previewRows.some(row => {
      const target = mapping[row.temp_id];
      return target && target !== 'NEW' && target !== 'SKIP';
    })
  ), [previewRows, mapping]);

  const hasAppendSelections = useMemo(() => (
    previewRows.some(row => mapping[row.temp_id] === 'NEW')
  ), [previewRows, mapping]);

  const allChaptersSkipped = useMemo(() => (
    previewRows.length > 0 &&
    previewRows.every(row => !mapping[row.temp_id] || mapping[row.temp_id] === 'SKIP')
  ), [previewRows, mapping]);

  const handleCommit = () => {
    if (!userId) {
      message.error('Please sign in before uploading new chapters.');
      return;
    }
    if (!file) {
      message.error(requiredFileCopy);
      return;
    }
    if (!previewRows.length) {
      message.warning('Generate a preview before saving changes.');
      return;
    }

    const selections: ChapterSelection[] = previewRows
      .filter(row => mapping[row.temp_id] !== 'SKIP')
      .map(row => ({
        tempId: row.temp_id,
        targetChapterGuid: mapping[row.temp_id] && mapping[row.temp_id] !== 'NEW' ? mapping[row.temp_id] : null,
        expectedVersion: mapping[row.temp_id] && mapping[row.temp_id] !== 'NEW'
          ? chapterVersions[mapping[row.temp_id]] || undefined
          : undefined,
      }));

    if (!selections.length) {
      message.warning(isEpubUpload ? 'Select at least one chapter to replace.' : 'Select at least one chapter to append or replace.');
      return;
    }

    if (isEpubUpload && selections.some(sel => !sel.targetChapterGuid)) {
      message.error('You can only replace existing chapters for books uploaded as EPUBs.');
      return;
    }

    const summaries = selections
      .filter(sel => sel.targetChapterGuid)
      .map(sel => {
        const targetGuid = sel.targetChapterGuid as string;
        const targetLabel = existingChapterMap[targetGuid]?.label || 'Unknown chapter';
        const uploadTitle = previewRowMap[sel.tempId]?.title || 'Uploaded chapter';
        return {
          tempId: sel.tempId,
          targetGuid,
          targetLabel,
          uploadTitle,
        };
      });

    if (summaries.length > 0) {
      setPendingSelections(selections);
      setReplacementSummaries(summaries);
      setIsReplaceConfirmVisible(true);
      return;
    }

    setPendingSelections(null);
    setReplacementSummaries([]);
    void performCommit(selections);
  };

  const handleReplaceConfirm = () => {
    if (!pendingSelections) {
      setIsReplaceConfirmVisible(false);
      return;
    }
    const selectionsToCommit = pendingSelections;
    setIsReplaceConfirmVisible(false);
    setPendingSelections(null);
    setReplacementSummaries([]);
    void performCommit(selectionsToCommit);
  };

  const handleReplaceCancel = () => {
    setIsReplaceConfirmVisible(false);
    setPendingSelections(null);
    setReplacementSummaries([]);
  };

  const handleBulkMapping = (value: 'NEW' | 'SKIP') => {
    if (value === 'NEW' && !allowAppend) {
      message.info('Appending new chapters is unavailable for books uploaded as EPUBs.');
      return;
    }
    setMapping(prev => {
      const updated: Record<string, string> = { ...prev };
      previewRows.forEach(row => {
        updated[row.temp_id] = value;
      });
      return updated;
    });
  };

  const columns: ColumnsType<PreviewRow> = [
    {
      title: 'Chapter',
      dataIndex: 'title',
      key: 'title',
      render: (_: string, record: PreviewRow) => (
        <div>
          <Text strong>{record.title || `Chapter ${record.chapter_number}`}</Text>
          <Paragraph style={{ marginBottom: 0 }} type="secondary">
            {record.snippet
              ? `${record.snippet.slice(0, 160)}${record.snippet.length > 160 ? '…' : ''}`
              : 'No preview available.'}
          </Paragraph>
        </div>
      ),
    },
    {
      title: 'Words',
      dataIndex: 'word_count',
      key: 'word_count',
      width: 120,
      render: (_: number, record: PreviewRow) => (
        <Text>{record.word_count.toLocaleString()}</Text>
      ),
    },
    {
      title: 'Map to Chapter',
      dataIndex: 'temp_id',
      key: 'mapping',
      width: 260,
      render: (tempId: string) => (
        <Select
          value={mapping[tempId] || 'SKIP'}
          onChange={(value) => setMapping(prev => ({ ...prev, [tempId]: value }))}
          style={{ width: '100%' }}
        >
          {allowAppend && <Select.Option value="NEW">Append as new chapter</Select.Option>}
          <Select.Option value="SKIP">Skip this chapter</Select.Option>
          {existingChapterOptions.map(option => (
            <Select.Option
              key={option.value}
              value={option.value}
              disabled={selectedTargets.has(option.value) && mapping[tempId] !== option.value}
            >
              {option.label}
            </Select.Option>
          ))}
        </Select>
      ),
    },
  ];

  return (
    <>
      <Modal
        className="chapter-upload-modal"
        open={visible}
        onCancel={onClose}
        width={1000}
        title={<div className="translation-modal-titlebar"><span className="translation-modal-titlebar__title">Upload Chapters</span></div>}
        destroyOnClose
        style={{ top: 40 }}
        bodyStyle={{ maxHeight: 'min(76vh, calc(100vh - 220px))', overflowY: 'auto', paddingBottom: 12 }}
        footer={
          <div
            data-tour-id="chapter-upload-footer"
            data-testid="tutorial:chapter-upload:footer"
            className="chapter-upload-modal-footer"
          >
            <div className="translation-modal-footer-summary" aria-live="polite">
              <span>
                <strong>{file ? '1' : '0'}</strong> file selected
              </span>
              <span className="translation-modal-footer-summary__separator">·</span>
              <span>
                <strong>{previewRows.length}</strong> detected chapters
              </span>
              <span className="translation-modal-footer-summary__separator">·</span>
              {!previewRows.length ? (
                <span className="translation-modal-footer-summary__warning">Preview needed</span>
              ) : allChaptersSkipped ? (
                <span className="translation-modal-footer-summary__warning">Mapping needed</span>
              ) : hasReplacementSelections ? (
                <span className="translation-modal-footer-summary__warning">Replacement selected</span>
              ) : (
                <span>Ready to save</span>
              )}
            </div>
            <div className="chapter-upload-modal-footer__actions">
            <Button onClick={onClose} disabled={isPreviewLoading || isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleGeneratePreview}
              icon={<UploadOutlined />}
              loading={isPreviewLoading}
              disabled={!file}
              data-tour-id="chapter-upload-preview-action"
              data-testid="tutorial:chapter-upload:generate-preview"
            >
              Generate Preview
            </Button>
            <Button
              type="primary"
              onClick={handleCommit}
              loading={isSubmitting}
              disabled={!previewRows.length}
              data-tour-id="chapter-upload-save-action"
              data-testid="tutorial:chapter-upload:save"
            >
              Save Changes
            </Button>
            </div>
          </div>
        }
      >
        <div ref={modalContentRef} className="translation-modal-body chapter-upload-modal-body" data-testid="tutorial:chapter-upload:modal">
          <Space direction="vertical" style={{ width: '100%', paddingBottom: 2 }} size="middle">
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="link"
                size="small"
                icon={<QuestionCircleOutlined />}
                onClick={handleOpenUploadTour}
                className="app-tour-trigger"
              >
                Take a Tour
              </Button>
            </div>
            <section
              data-tour-id="chapter-upload-summary"
              data-testid="tutorial:chapter-upload:summary"
              className="translation-modal-block"
            >
              <div className="translation-modal-block-header">
                <div>
                  <span className="translation-modal-kicker">Scope</span>
                  <h3>Upload and mapping readiness</h3>
                  <p>Preview the uploaded file, then map detected chapters to append, replace, or skip.</p>
                </div>
                <span className={`chapter-upload-readiness-pill ${previewRows.length && !allChaptersSkipped ? 'is-ready' : 'is-needed'}`}>
                  {previewRows.length && !allChaptersSkipped ? 'Ready' : 'Needed'}
                </span>
              </div>
              <Paragraph style={{ marginBottom: 0 }}>
                {isEpubUpload
                  ? 'Upload an updated EPUB file to replace the chapters in this manuscript. This book originated from an EPUB, so you can only overwrite existing chapters.'
                  : 'Upload a DOCX or EPUB with the chapters you want to append or replace. We will parse the file and let you map each detected chapter to your existing manuscript.'}
              </Paragraph>

              {previewRows.length > 0 && (() => {
                let alertType: 'info' | 'warning' = 'info';
                let alertMessage = '';

                if (allChaptersSkipped) {
                  alertMessage = isEpubUpload
                    ? 'All detected chapters are currently skipped. Map at least one uploaded chapter to an existing chapter to replace it.'
                    : 'All detected chapters are currently skipped. Map at least one uploaded chapter to append or replace before saving.';
                } else if (hasReplacementSelections && hasAppendSelections) {
                  alertType = 'warning';
                  alertMessage = 'Some uploaded chapters will overwrite existing chapters (deleting translated/proofread versions) while others will be appended to the end of your manuscript.';
                } else if (hasReplacementSelections) {
                  alertType = 'warning';
                  alertMessage = 'Replacing a chapter will delete any translated or proofread versions tied to it. This cannot be undone.';
                } else if (hasAppendSelections) {
                  alertMessage = 'Selected chapters will be appended to the end of your manuscript without touching existing chapters.';
                } else {
                  alertMessage = 'All detected chapters are currently skipped. Map at least one uploaded chapter to proceed.';
                }

                return (
                  <Alert
                    type={alertType}
                    showIcon
                    message={alertMessage}
                    style={{ marginBottom: 0 }}
                  />
                );
              })()}

              {previewError && (
                <Alert type="error" message={previewError} />
              )}

              {!!skippedChapters.length && (
                <Alert
                  type="warning"
                  message={`Skipped chapters: ${skippedChapters.join(', ')}. These sections did not contain readable text after cleaning.`}
                />
              )}
            </section>
            <section className="translation-modal-block" data-tour-id="chapter-upload-dropzone" data-testid="tutorial:chapter-upload:file">
              <div className="translation-modal-block-header">
                <div>
                  <span className="translation-modal-kicker">File</span>
                  <h3>Manuscript source</h3>
                  <p>Select the updated manuscript file to parse before saving any chapter changes.</p>
                </div>
                <span className={`chapter-upload-readiness-pill ${file ? 'is-ready' : 'is-needed'}`}>
                  {file ? 'Selected' : 'File needed'}
                </span>
              </div>
              <input
                key={fileKey}
                ref={fileInputRef}
                type="file"
                accept={isEpubUpload ? '.epub' : '.docx,.epub'}
                onChange={handleFileChange}
                aria-label="Upload file"
                style={{ display: 'none' }}
                data-testid="tutorial:chapter-upload:file-input"
              />
              <div
                className="chapter-upload-dropzone-trigger"
                data-testid="tutorial:chapter-upload:dropzone"
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                onDragOver={handleDragOver}
                onDragEnter={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `1px dashed ${isDragOver ? token.colorPrimary : token.colorBorder}`,
                  borderRadius: 6,
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  background: isDragOver ? token.colorFillSecondary : token.colorFillQuaternary,
                  minHeight: 48,
                }}
              >
                <UploadOutlined style={{ color: token.colorTextTertiary }} />
                <div style={{ flex: 1 }}>
                  <Text strong style={{ display: 'block', lineHeight: 1.2, color: token.colorText }}>
                    Drag & drop a file, or click to browse
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, color: token.colorTextSecondary }}>
                    {file ? `Selected: ${file.name}` : isEpubUpload ? 'EPUB files only.' : 'DOCX or EPUB files.'}
                  </Text>
                </div>
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Browse
                </Button>
              </div>
            </section>

            {previewRows.length > 0 && (
              <section className="translation-modal-block" data-tour-id="chapter-upload-mapping" data-testid="tutorial:chapter-upload:mapping">
                <div className="translation-modal-block-header">
                  <div>
                    <span className="translation-modal-kicker">Mapping</span>
                    <h3>Detected chapters</h3>
                    <p>Choose what each uploaded chapter will change before committing.</p>
                  </div>
                  <span className={`chapter-upload-readiness-pill ${allChaptersSkipped ? 'is-needed' : 'is-ready'}`}>
                    {allChaptersSkipped ? 'Mapping needed' : 'Mapped'}
                  </span>
                </div>
                <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                  <Button size="small" onClick={() => handleBulkMapping('SKIP')}>
                    Skip All
                  </Button>
                  <Tooltip title={allowAppend ? 'Append every parsed chapter to the end of your manuscript.' : 'Appending new chapters is unavailable for EPUB uploads.'}>
                    <Button size="small" onClick={() => handleBulkMapping('NEW')} disabled={!allowAppend}>
                      Append All
                    </Button>
                  </Tooltip>
                </Space>
                <Table
                  rowKey="temp_id"
                  columns={columns}
                  dataSource={previewRows}
                  pagination={false}
                  size="small"
                  scroll={{ y: 420 }}
                  sticky
                />
              </section>
            )}
          </Space>
        </div>
      </Modal>

      <Modal
        className="replace-chapters-modal"
        open={isReplaceConfirmVisible}
        onCancel={handleReplaceCancel}
        title="Replace existing chapters?"
        width={520}
        destroyOnClose
        centered
        footer={[
          <Button key="cancel" onClick={handleReplaceCancel} disabled={isSubmitting}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            onClick={handleReplaceConfirm}
            loading={isSubmitting}
          >
            Replace and overwrite
          </Button>,
        ]}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="Replacing a chapter deletes every translated or proofread version tied to it."
            description="Double-check the mappings below before continuing."
          />
          <div className="replace-chapters-modal__list">
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              Chapters to overwrite
            </Text>
            {replacementSummaries.length ? (
              <ul>
                {replacementSummaries.map(item => (
                  <li key={`${item.tempId}-${item.targetGuid}`}>
                    <Text strong>{item.uploadTitle}</Text>
                    <span> → {item.targetLabel}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                No chapters selected for replacement.
              </Paragraph>
            )}
          </div>
        </Space>
      </Modal>
      <AppTour
        open={uploadTourOpen}
        steps={uploadTourSteps}
        current={uploadTourStep}
        onChange={setUploadTourStep}
        onClose={handleUploadTourDismiss}
        onFinish={handleUploadTourDismiss}
        zIndex={2600}
      />
    </>
  );
};

export default ChapterUploadModal;
